import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Text } from '@cuemath/web-comps';

import COLORS from '../../constants/colors';
import ReassignSpecialist from './reassign-specialist';
import TriggerHandoff from './trigger-handoff';
import { EVAL_MODAL_NAMES } from './constants';

const Page = styled.div`
  padding: 24px 28px 40px;
`;

const Intro = styled.div`
  margin-bottom: 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(268px, 1fr));
  gap: 16px;
  max-width: 1160px;
`;

const Card = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  text-align: left;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-left: 3px solid ${COLORS.BOSTON_BLUE};
  border-radius: 6px;
  padding: 16px 18px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: 0 2px 10px rgba(28, 26, 23, 0.1);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.BOSTON_BLUE};
    outline-offset: 2px;
  }
`;

const CARDS = [
  {
    label: 'Reassign Specialist',
    hint: 'Move a booked eval to another specialist at the same slot.',
    modal: EVAL_MODAL_NAMES.reassignSpecialist,
  },
  {
    label: 'Specialist Roster',
    hint: 'Who is on the bench this month, and when they are paused.',
    link: '/eval/specialist-roster/',
  },
  {
    label: 'Evals',
    hint: 'Upcoming and completed evals, by month and specialist.',
    link: '/eval/evals/',
  },
  {
    label: 'Specialist Availability',
    hint: 'Who is free, booked or paused over the next 7 days, in any timezone.',
    link: '/eval/availability/',
  },
  {
    label: 'Capacity',
    hint: 'Slots given, filled and available per specialist.',
    link: '/eval/capacity/',
  },
  {
    label: 'Coach Mapping',
    hint: 'Who has a coach and who does not, with slot and coach changes.',
    link: '/eval/failed-mappings/',
  },
  {
    label: 'Trigger Student Handoff',
    hint: "Raise a coach's prep card when the enrollment never did.",
    modal: EVAL_MODAL_NAMES.triggerHandoff,
  },
  {
    label: 'Prep Tasks',
    hint: 'Handoff and readiness per coach, and who is late.',
    link: '/eval/prep-tasks/',
  },
  {
    label: 'Prep Compliance',
    hint: 'Violations raised for missed eval prep.',
    link: '/eval/prep-compliance/',
  },
  {
    label: 'Parent Class Feedback',
    hint: 'What parents said about a class: rating, comfort and pace.',
    link: '/eval/parent-feedback/',
  },
];

const Eval = () => {
  const history = useHistory();
  const [modalName, setModalName] = React.useState(null);

  const closeModal = React.useCallback(() => {
    setModalName(null);
  }, []);

  return (
    <Page>
      <Intro>
        <Text variant="h6">Eval</Text>
        <Text variant="body3" color={COLORS.GRAY}>
          Specialist supply, bookings and capacity for the eval flow.
        </Text>
      </Intro>

      <Grid>
        {CARDS.map(({ label, hint, modal, link }) => (
          <Card
            key={modal || link}
            onClick={() => (link ? history.push(link) : setModalName(modal))}
          >
            <Text variant="body1Medium">{label}</Text>
            <Text variant="body3" color={COLORS.GRAY}>
              {hint}
            </Text>
          </Card>
        ))}
      </Grid>

      {modalName === EVAL_MODAL_NAMES.reassignSpecialist && (
        <ReassignSpecialist closeModal={closeModal} />
      )}

      {modalName === EVAL_MODAL_NAMES.triggerHandoff && (
        <TriggerHandoff closeModal={closeModal} />
      )}
    </Page>
  );
};

export default Eval;
